import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FilePenLine, Send, Search, Info, MoreVertical, Eye, FileCheck2, BellRing, Ban, MessageSquareReply, CheckCircle2, Clock, AlertTriangle, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const FILE_TIMEOUT = 120000;
const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export const LETTER_STATUS = {
  "Not opened": { cls: "bg-slate-100 text-slate-700", icon: Clock },
  Opened: { cls: "bg-sky-100 text-sky-800", icon: Eye },
  Overdue: { cls: "bg-red-100 text-red-800", icon: AlertTriangle },
  "Query raised": { cls: "bg-amber-100 text-amber-800", icon: MessageCircleQuestion },
  Accepted: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  Acknowledged: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  Withdrawn: { cls: "bg-slate-100 text-slate-500 line-through", icon: Ban },
};

export function LetterStatusPill({ status }) {
  const s = LETTER_STATUS[status] || LETTER_STATUS["Not opened"];
  const Icon = s.icon;
  return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap", s.cls)}><Icon className="h-3 w-3" />{status}</span>;
}

const FILTERS = [["", "All"], ["waiting", "Waiting"], ["overdue", "Overdue"], ["query", "Queries"], ["done", "Accepted"], ["withdrawn", "Withdrawn"]];
const form0 = { employee_id: "", letter_type: "", title: "", message: "", response: "", respond_days: 7, contract_extended_until: "" };

async function openBlob(url, params) {
  const t = toast.loading("Opening…");
  try {
    const res = await api.get(url, { responseType: "blob", timeout: FILE_TIMEOUT, params });
    const href = URL.createObjectURL(res.data);
    window.open(href, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(href), 60000);
    toast.dismiss(t);
  } catch { toast.error("Couldn't open the file — try again", { id: t }); }
}

function Thread({ items }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-2">
      {items.map((m) => (
        <div key={m.id} className={cn("rounded-md border px-3 py-2 text-sm", m.by === "hr" ? "bg-teal/5 border-teal/30 ml-6" : "bg-amber-50 border-amber-200 mr-6")}>
          <p className="text-[11px] text-slate-500 mb-0.5">
            {m.by === "hr" ? "HR" : "Employee"} · {m.by_name} · {fmtDateTime(m.at)}
            {m.kind === "revised" && <span className="ml-1 font-semibold text-teal">· sent revised letter v{m.version}</span>}
          </p>
          <p className="text-slate-700 whitespace-pre-wrap">{m.text}</p>
        </div>
      ))}
    </div>
  );
}

function DetailDialog({ letter, onClose, onChanged }) {
  const fileRef = useRef(null);
  const [reply, setReply] = useState("");
  const [days, setDays] = useState(7);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [mode, setMode] = useState(null); // "reply" | "withdraw"

  const replyMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("message", reply);
      fd.append("respond_days", String(days || 0));
      if (fileRef.current?.files?.[0]) fd.append("file", fileRef.current.files[0]);
      return (await api.post(`/issued-letters/${letter.id}/reply`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: FILE_TIMEOUT })).data;
    },
    onSuccess: (d) => { toast.success(d.version > letter.version ? `Revised letter v${d.version} sent` : "Reply sent"); onChanged(); onClose(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const withdrawMut = useMutation({
    mutationFn: async () => (await api.post(`/issued-letters/${letter.id}/withdraw`, { reason: withdrawReason })).data,
    onSuccess: () => { toast.success("Letter withdrawn — the employee has been told"); onChanged(); onClose(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (!letter) return null;
  const done = letter.status === "Accepted" || letter.status === "Acknowledged";
  const open = letter.status === "Issued" || letter.status === "Query";
  return (
    <Dialog open={!!letter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-navy pr-6">{letter.title}</DialogTitle>
          <DialogDescription>{letter.letter_no} · v{letter.version} · {letter.letter_type} · {letter.employee_name} ({letter.employee_code})</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div><p className="overline text-slate-400">Status</p><LetterStatusPill status={letter.display_status} /></div>
          <div><p className="overline text-slate-400">Employee must</p><p className="text-navy">{letter.response === "acknowledge" ? "Acknowledge receipt" : "Accept"}</p></div>
          <div><p className="overline text-slate-400">Issued</p><p>{fmtDateTime(letter.issued_at)} by {letter.issued_by_name}</p></div>
          <div><p className="overline text-slate-400">Respond by</p><p>{fmtDate(letter.due_date)}</p></div>
          <div><p className="overline text-slate-400">First opened</p><p>{letter.first_viewed_at ? `${fmtDateTime(letter.first_viewed_at)} (${letter.view_count}×)` : "Not yet"}</p></div>
          {done && <div><p className="overline text-slate-400">{letter.status} on</p><p>{fmtDateTime(letter.responded_at)} · signed “{letter.typed_name}”</p></div>}
          {letter.contract_extended_until && <div className="col-span-2"><p className="overline text-slate-400">Contract extended until</p>
            <p>{fmtDate(letter.contract_extended_until)} {letter.contract_updated ? <span className="text-emerald-700">· updated on the employee record</span> : <span className="text-slate-400">· updates the employee record when accepted</span>}</p></div>}
          {letter.message && <div className="col-span-2"><p className="overline text-slate-400">Note to employee</p><p className="text-slate-700 whitespace-pre-wrap">{letter.message}</p></div>}
          {letter.response_remarks && <div className="col-span-2"><p className="overline text-slate-400">Employee's remarks</p><p className="text-slate-700">{letter.response_remarks}</p></div>}
          {letter.status === "Withdrawn" && <div className="col-span-2"><p className="overline text-slate-400">Withdrawn</p><p>{fmtDateTime(letter.withdrawn_at)} — {letter.withdraw_reason}</p></div>}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => openBlob(`/issued-letters/${letter.id}/file`)}><Eye className="h-4 w-4 mr-1" /> View letter v{letter.version}</Button>
          {(letter.previous_versions || []).map((v) => (
            <Button key={v} size="sm" variant="ghost" onClick={() => openBlob(`/issued-letters/${letter.id}/file`, { version: v })}>v{v}</Button>
          ))}
          {letter.has_accepted_copy && <Button size="sm" className="bg-teal hover:bg-teal/90 text-white" onClick={() => openBlob(`/issued-letters/${letter.id}/file`, { copy: "accepted" })}><FileCheck2 className="h-4 w-4 mr-1" /> {letter.status === "Acknowledged" ? "Acknowledged" : "Accepted"} copy</Button>}
        </div>

        {letter.thread?.length > 0 && <div><p className="overline text-slate-400 mb-2">Conversation</p><Thread items={letter.thread} /></div>}

        {open && mode === null && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <Button size="sm" className="bg-navy" onClick={() => setMode("reply")}><MessageSquareReply className="h-4 w-4 mr-1" /> {letter.status === "Query" ? "Reply to query" : "Send a note / revised letter"}</Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => setMode("withdraw")}><Ban className="h-4 w-4 mr-1" /> Withdraw</Button>
          </div>
        )}
        {mode === "reply" && (
          <div className="space-y-3 rounded-md border border-slate-200 p-3">
            <div><Label>Your reply to the employee *</Label>
              <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} className="mt-1" placeholder="e.g. Corrected the date to 31 March 2027 — please accept the revised letter." /></div>
            <div><Label>Revised letter (optional — PDF or photo)</Label>
              <Input type="file" ref={fileRef} accept=".pdf,.png,.jpg,.jpeg,.webp" className="mt-1" />
              <p className="text-[11px] text-slate-400 mt-1">Attaching a file sends it as a new version. The employee must open and accept the new one.</p></div>
            <div className="w-40"><Label>Respond within (days)</Label><Input type="number" min={1} max={60} value={days} onChange={(e) => setDays(Number(e.target.value))} className="mt-1" /></div>
            <div className="flex gap-2"><Button size="sm" className="bg-navy" disabled={replyMut.isPending || reply.trim().length < 3} onClick={() => replyMut.mutate()}>{replyMut.isPending ? "Sending…" : "Send"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button></div>
          </div>
        )}
        {mode === "withdraw" && (
          <div className="space-y-3 rounded-md border border-red-200 bg-red-50/40 p-3">
            <div><Label>Reason (shown to the employee) *</Label>
              <Input value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)} className="mt-1" placeholder="e.g. Sent to the wrong person" /></div>
            <div className="flex gap-2"><Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={withdrawMut.isPending || withdrawReason.trim().length < 3} onClick={() => withdrawMut.mutate()}>Withdraw letter</Button>
              <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ open, onOpenChange, meta, onIssued }) {
  const fileRef = useRef(null);
  const [f, setF] = useState(form0);
  const { data: people, isLoading: loadingPeople } = useQuery({
    queryKey: ["letterEmployees"], enabled: open,
    queryFn: async () => (await api.get("/employees")).data,
  });
  const staff = useMemo(() => (people || [])
    .filter((e) => !["Left", "Terminated", "Retired"].includes(e.employment?.employment_status) && e.record_status !== "Archived")
    .sort((a, b) => (a.identity?.name || "").localeCompare(b.identity?.name || "")), [people]);
  const emp = staff.find((e) => e.id === f.employee_id);
  const isExtension = f.letter_type === meta?.contract_extension_type;
  const typeDefault = meta?.types?.find((t) => t.name === f.letter_type)?.response || "accept";
  const response = f.response || typeDefault;

  const setType = (v) => setF((p) => ({ ...p, letter_type: v, response: "",
    title: p.title && !p.autoTitle ? p.title : (emp ? `${v.replace(" Letter", "")} — ${emp.identity?.name}` : v), autoTitle: true }));
  const setEmp = (v) => {
    const e = staff.find((x) => x.id === v);
    setF((p) => ({ ...p, employee_id: v, title: p.letter_type && (p.autoTitle || !p.title) ? `${p.letter_type.replace(" Letter", "")} — ${e?.identity?.name}` : p.title, autoTitle: p.autoTitle || !p.title }));
  };
  const close = (v) => { if (!v) { setF(form0); if (fileRef.current) fileRef.current.value = ""; } onOpenChange(v); };

  const issue = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", fileRef.current.files[0]);
      ["employee_id", "letter_type", "title", "message"].forEach((k) => fd.append(k, f[k] || ""));
      fd.append("response", response);
      fd.append("respond_days", String(f.respond_days || 7));
      fd.append("contract_extended_until", isExtension ? f.contract_extended_until : "");
      return (await api.post("/issued-letters", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: FILE_TIMEOUT })).data;
    },
    onSuccess: (d) => { toast.success(`${d.letter_no} sent to ${d.employee_name} — they've been notified`); onIssued(); close(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const submit = () => {
    if (!f.employee_id) return toast.error("Choose the employee");
    if (!f.letter_type) return toast.error("Choose the letter type");
    if (!f.title.trim()) return toast.error("Give the letter a title");
    if (!fileRef.current?.files?.length) return toast.error("Attach the letter (PDF)");
    if (isExtension && !f.contract_extended_until) return toast.error("Enter the date the contract is extended until");
    issue.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="issue-letter-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-navy">Issue a letter</DialogTitle>
          <DialogDescription>The employee is notified, reads it in the portal and accepts it there. The accepted copy is filed in their documents automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Employee *</Label>
            <Select value={f.employee_id} onValueChange={setEmp}>
              <SelectTrigger className="mt-1" data-testid="letter-employee"><SelectValue placeholder={loadingPeople ? "Loading…" : "Select employee"} /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {staff.map((e) => <SelectItem key={e.id} value={e.id}>{e.identity?.name}{e.employee_id ? ` (${e.employee_id})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {emp && !emp.user_id && <p className="text-xs text-red-600 mt-1">This employee has no portal login yet — create one under User Accounts first.</p>}
          </div>
          <div><Label>Letter type *</Label>
            <Select value={f.letter_type} onValueChange={setType}>
              <SelectTrigger className="mt-1" data-testid="letter-type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent className="bg-white">{(meta?.types || []).map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value, autoTitle: false })} className="mt-1" maxLength={150} placeholder="e.g. Contract Extension — Renu" /></div>
          <div><Label>Letter file * (PDF recommended, or a JPG/PNG photo — max 15 MB)</Label>
            <Input type="file" ref={fileRef} accept=".pdf,.png,.jpg,.jpeg,.webp" className="mt-1" data-testid="letter-file" />
            <p className="text-[11px] text-slate-400 mt-1">Made the letter in Word? Use File → Save as PDF. It needs your signature/stamp on it just like a printed letter.</p></div>
          {isExtension && (
            <div className="rounded-md border border-teal/30 bg-teal/5 p-3">
              <Label>Contract extended until *</Label>
              <Input type="date" value={f.contract_extended_until} onChange={(e) => setF({ ...f, contract_extended_until: e.target.value })} className="mt-1 w-48" data-testid="letter-extended-until" />
              <p className="text-[11px] text-slate-500 mt-1">When the employee accepts, "Contract Extended Until" on their Employee Master is updated to this date automatically.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Employee must</Label>
              <Select value={response} onValueChange={(v) => setF({ ...f, response: v })} disabled={isExtension}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="accept">Accept the letter</SelectItem><SelectItem value="acknowledge">Acknowledge receipt only</SelectItem></SelectContent>
              </Select></div>
            <div><Label>Respond within (days)</Label><Input type="number" min={1} max={meta?.max_respond_days || 60} value={f.respond_days} onChange={(e) => setF({ ...f, respond_days: Number(e.target.value) })} className="mt-1" /></div>
          </div>
          {response === "acknowledge" && <p className="text-[11px] text-slate-500 -mt-2">Use "acknowledge" for warning / show-cause letters and notices: the employee confirms receipt, not agreement.</p>}
          <div><Label>Note to the employee (optional)</Label>
            <Textarea rows={2} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="mt-1" placeholder="e.g. Your contract is extended on the same terms. Please accept by Friday." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>Cancel</Button>
          <Button className="bg-navy" disabled={issue.isPending} onClick={submit} data-testid="issue-letter-submit"><Send className="h-4 w-4 mr-1" /> {issue.isPending ? "Sending…" : "Send to employee"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IssuedLetters() {
  const qc = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null);
  const meta = useQuery({ queryKey: ["letterMeta"], queryFn: async () => (await api.get("/issued-letters/meta")).data });
  const list = useQuery({ queryKey: ["issuedLetters"], queryFn: async () => (await api.get("/issued-letters")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["issuedLetters"] }); qc.invalidateQueries({ queryKey: ["adminActionCounts"] }); };

  const remind = useMutation({
    mutationFn: async (l) => (await api.post(`/issued-letters/${l.id}/remind`)).data,
    onSuccess: () => toast.success("Reminder sent"),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const counts = list.data?.counts || {};
  const groups = { waiting: ["Not opened", "Opened", "Overdue"], overdue: ["Overdue"], query: ["Query raised"], done: ["Accepted", "Acknowledged"], withdrawn: ["Withdrawn"] };
  const t = search.trim().toLowerCase();
  const rows = (list.data?.items || []).filter((l) => (!filter || groups[filter].includes(l.display_status))
    && (!t || [l.employee_name, l.employee_code, l.letter_no, l.title, l.letter_type].some((x) => (x || "").toLowerCase().includes(t))));
  const detail = (list.data?.items || []).find((l) => l.id === detailId) || null;

  return (
    <div>
      <PageHeader overline="Employee Letters" title="Letters for Acceptance" icon={FilePenLine}
        subtitle="Send a letter to an employee to accept online — no printing, scanning or courier."
        actions={<Button className="bg-navy" onClick={() => setIssueOpen(true)} data-testid="issue-letter-btn"><Send className="h-4 w-4 mr-1" /> Issue a letter</Button>} />

      <div className="mb-5 flex items-start gap-3 rounded-lg border border-teal/30 bg-teal/5 px-4 py-3 text-sm text-slate-600">
        <Info className="h-4 w-4 mt-0.5 text-teal shrink-0" />
        <p>Upload the signed letter as a <span className="font-medium">PDF</span>. The employee gets a notification, opens it under <span className="font-medium">My Letters</span>, types their name and accepts — or asks you a question. On acceptance, an <span className="font-medium">accepted copy</span> (the letter + an Electronic Acceptance Record with date, time, IP address and device) is filed in their documents. A contract extension also updates "Contract Extended Until" automatically.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Waiting</p><p className="text-2xl font-semibold text-navy">{counts.waiting ?? "—"}</p></Card>
        <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Overdue</p><p className="text-2xl font-semibold text-red-600">{counts.overdue ?? "—"}</p></Card>
        <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Queries to answer</p><p className="text-2xl font-semibold text-amber-600">{counts.query ?? "—"}</p></Card>
        <Card className="p-4 shadow-none border-slate-200"><p className="overline text-slate-400">Accepted</p><p className="text-2xl font-semibold text-emerald-700">{counts.done ?? "—"}</p></Card>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k || "all"} onClick={() => setFilter(k)}
              className={cn("rounded-full border px-3 py-1 text-sm", filter === k ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal")}>
              {label}{k === "query" && counts.query ? ` (${counts.query})` : ""}
            </button>
          ))}
        </div>
        <div className="relative md:ml-auto md:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee, letter no., title…" className="pl-9 focus-visible:ring-teal" />
        </div>
      </div>

      {list.isLoading ? <Loading /> : list.isError ? <ErrorState message="Couldn't load letters." onRetry={list.refetch} /> :
        (list.data?.items || []).length === 0 ? (
          <EmptyState icon={FilePenLine} title="No letters issued yet"
            hint="Issue a contract extension, appointment, increment or any other letter for an employee to accept online."
            action={<Button className="bg-navy" onClick={() => setIssueOpen(true)}><Send className="h-4 w-4 mr-1" /> Issue the first letter</Button>} />
        ) : rows.length === 0 ? <EmptyState icon={Search} title="Nothing matches" /> : (
          <Card className="border-slate-200 shadow-none overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Letter</TableHead>
                <TableHead className="overline text-slate-500">Employee</TableHead>
                <TableHead className="overline text-slate-500">Issued</TableHead>
                <TableHead className="overline text-slate-500">Respond by</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="text-right overline text-slate-500"> </TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l.id} className="table-row-i cursor-pointer" onClick={() => setDetailId(l.id)} data-testid={`letter-row-${l.letter_no}`}>
                    <TableCell><p className="font-medium text-navy">{l.title}</p><p className="text-[11px] text-slate-400">{l.letter_no} · v{l.version} · {l.letter_type}</p></TableCell>
                    <TableCell><p>{l.employee_name}</p><p className="text-[11px] text-slate-400">{l.employee_code}</p></TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{fmtDate(l.issued_at)}</TableCell>
                    <TableCell className="text-sm text-slate-600 whitespace-nowrap">{["Accepted", "Acknowledged", "Withdrawn"].includes(l.status) ? "—" : fmtDate(l.due_date)}</TableCell>
                    <TableCell><LetterStatusPill status={l.display_status} />
                      {l.responded_at && <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(l.responded_at)}</p>}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailId(l.id)}><Info className="h-4 w-4 mr-2" /> Details & conversation</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openBlob(`/issued-letters/${l.id}/file`)}><Eye className="h-4 w-4 mr-2" /> View letter</DropdownMenuItem>
                          {l.has_accepted_copy && <DropdownMenuItem onClick={() => openBlob(`/issued-letters/${l.id}/file`, { copy: "accepted" })}><FileCheck2 className="h-4 w-4 mr-2" /> View accepted copy</DropdownMenuItem>}
                          {l.status === "Issued" && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => remind.mutate(l)}><BellRing className="h-4 w-4 mr-2" /> Send reminder</DropdownMenuItem></>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

      <IssueDialog open={issueOpen} onOpenChange={setIssueOpen} meta={meta.data} onIssued={refresh} />
      <DetailDialog key={detailId || "none"} letter={detail} onClose={() => setDetailId(null)} onChanged={refresh} />
    </div>
  );
}
